import type { DataGridColumn, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsBetaFeatureEnabled } from '@/entities/beta-feature';
import {
  CONVERSATION_IDS_FILTER_ID,
  type ConversationListItem,
  type ConversationSelectionSource,
  useConversationPersonNames,
} from '@/entities/conversations';
import { DEFAULT_DIALOG_SORTING } from '../constants';
import { buildColumnDescriptors } from './columnDescriptors';
import { describeFilter } from './describeFilter';
import { describeSorting } from './describeSorting';
import type { DescriptionPart } from './types';

/** Выборка диалогов, пришедшая переходом с показателя дашборда качества */
type Selection = {
  /** Подпись элемента показателя, по которому перешли */
  description: string;
  /** Показатель, с которого пришли — им выборка и названа */
  source: ConversationSelectionSource;
};

/** Параметры описания действующего отбора списка диалогов */
type Params = {
  /** Определения колонок таблицы — из них берутся подписи колонок и конфигурация их фильтров */
  columns: DataGridColumn<ConversationListItem>[];
  /** Действующий порядок колонок — в нём же перечисляются фильтры */
  columnOrder: string[];
  /** Действующие фильтры таблицы */
  filters: TableFilterState;
  /** Действующая сортировка таблицы */
  sorting: TableSortingState;
  /** Действующая выборка перехода; `null` — переход с дашборда ни при чём */
  selection?: Selection | null;
};

/**
 * Название показателя дашборда по источнику выборки. Показатель с готовой фразой
 * (красные зоны, авто-инсайты) названия не имеет — в описание идёт сама фраза.
 */
const SELECTION_SOURCE_LABELS = {
  consultantRating: 'consultant',
  lowScore: 'consultant',
  criteriaAverage: 'criterion',
  qualityZone: 'qualityZone',
  distribution: 'scoreRange',
  trend: 'period',
  alert: null,
  autoInsight: null,
  customChart: null,
} as const satisfies Record<ConversationSelectionSource, string | null>;

/**
 * Собирает описание действующего отбора списка диалогов частями: сортировка, за ней заданные
 * фильтры в порядке колонок таблицы. `null` — описания нет: либо фильтров нет, либо описание
 * отключено бета-функцией.
 *
 * Текст выводится из самого состояния таблицы, поэтому одному состоянию всегда соответствует
 * одно и то же описание.
 */
export const useFiltersDescription = ({
  columns,
  columnOrder,
  filters,
  sorting,
  selection,
}: Params): DescriptionPart[] | null => {
  const { t, i18n } = useTranslation(['dialogs', 'common']);

  const isDescriptionEnabled = useIsBetaFeatureEnabled('DIALOGS_FILTERS_DESCRIPTION');

  const descriptors = useMemo(() => buildColumnDescriptors(columns), [columns]);

  // Имена выбранных участников описание получает само: отбор переживает перезагрузку страницы,
  // а участники могут не попасть ни в строки списка, ни в первую страницу справочника.
  // Пока описание отключено, имена не нужны — запрашивать их незачем.
  const selectedPersonIds = useMemo(() => {
    const employeeIds: number[] = [];
    const clientIds: number[] = [];
    if (!isDescriptionEnabled) return { employeeIds, clientIds };
    filters.forEach((filter) => {
      const role = descriptors.get(filter.id)?.meta?.personRole;
      if (!role) return;
      const ids = (Array.isArray(filter.value) ? filter.value : [filter.value])
        .map(Number)
        .filter((id) => Number.isInteger(id));
      (role === 'employee' ? employeeIds : clientIds).push(...ids);
    });
    return { employeeIds, clientIds };
  }, [descriptors, filters, isDescriptionEnabled]);

  const { names: personNames, isLoading: isPersonNamesLoading } = useConversationPersonNames(selectedPersonIds);

  return useMemo(() => {
    if (!isDescriptionEnabled) return null;

    const ctx = { t, i18n, personNames, isPersonNamesLoading };

    const descriptorIds = Array.from(descriptors.keys());
    const getPosition = (id: string): number => {
      const userIndex = columnOrder.indexOf(id);
      if (userIndex >= 0) return userIndex;
      const defaultIndex = descriptorIds.indexOf(id);
      // Колонки, которых пользователь не переставлял, идут за переставленными в своём порядке
      return defaultIndex >= 0 ? columnOrder.length + defaultIndex : Number.MAX_SAFE_INTEGER;
    };

    const filterParts: DescriptionPart[] = [];

    // Выборка перехода живёт среди фильтров, но полем в панели фильтров не представлена:
    // вместо номеров диалогов её называют показателем дашборда и его значением.
    const hasSelection = filters.some(
      (filter) => filter.id === CONVERSATION_IDS_FILTER_ID && Array.isArray(filter.value) && filter.value.length,
    );
    if (hasSelection) {
      const labelKey = selection ? SELECTION_SOURCE_LABELS[selection.source] : null;
      filterParts.push({
        id: CONVERSATION_IDS_FILTER_ID,
        kind: 'selection',
        label: labelKey ? t(`dialogs:dialogListSection.filtersDescription.selectionSource.${labelKey}`) : null,
        connector: labelKey ? t('dialogs:dialogListSection.filtersDescription.connector.equals') : null,
        values: [
          {
            text: selection?.description ?? t('dialogs:dialogListSection.filtersDescription.selectionUnnamed'),
          },
        ],
        isFoldable: false,
      });
    }

    filters
      .filter((filter) => filter.id !== CONVERSATION_IDS_FILTER_ID)
      .sort((a, b) => getPosition(a.id) - getPosition(b.id))
      .forEach((filter) => {
        const descriptor = descriptors.get(filter.id);
        if (!descriptor) return;
        const part = describeFilter(descriptor, filter.value, ctx);
        if (part) filterParts.push(part);
      });

    if (!filterParts.length) return null;

    // Сортировка открывает описание и называется всегда — в том числе сортировка по умолчанию.
    // Пустое состояние сортировки означает именно её: список в этом случае упорядочен так же,
    // как задаёт умолчание таблицы.
    const isDefaultSorting = !sorting.length;
    const sortingPart = describeSorting(
      isDefaultSorting ? DEFAULT_DIALOG_SORTING : sorting,
      isDefaultSorting,
      descriptors,
    );
    return sortingPart ? [sortingPart, ...filterParts] : filterParts;
  }, [
    isDescriptionEnabled,
    descriptors,
    columnOrder,
    filters,
    sorting,
    selection,
    personNames,
    isPersonNamesLoading,
    t,
    i18n,
  ]);
};
